export const PAYOUT_PATH_MANAGER = 'manager';
export const PAYOUT_PATH_DIRECT = 'direct';

function formatAuditDate(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) {
        return new Date().toISOString().split('T')[0];
    }
    return d.toISOString().split('T')[0];
}

function roundCurrency(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.round(parsed * 100) / 100;
}

export function formatManagerReleasePayReason(recipientName, amountPaid, amountOwedBefore, date = new Date()) {
    const recipient = String(recipientName || 'Manager').trim() || 'Manager';
    const paid = roundCurrency(amountPaid);
    const owed = roundCurrency(amountOwedBefore);
    const dateStr = formatAuditDate(date);

    if (owed > 0 && paid < owed) {
        return `Paid to Manager's Account (${recipient}) on ${dateStr} — $${paid.toFixed(2)} of $${owed.toFixed(2)} owed`;
    }

    return `Paid to Manager's Account (${recipient}) on ${dateStr} — $${paid.toFixed(2)}`;
}

export function formatDirectStripePayReason(recipientName, amountPaid, date = new Date()) {
    const recipient = String(recipientName || 'employee').trim() || 'employee';
    const paid = roundCurrency(amountPaid);
    const dateStr = formatAuditDate(date);
    return `Paid directly to ${recipient}'s Stripe account on ${dateStr} — $${paid.toFixed(2)}`;
}

export function formatTipPayoutDisplayLabel(manualPayReason) {
    if (manualPayReason === null || manualPayReason === undefined) {
        return null;
    }

    const trimmed = String(manualPayReason).trim();
    if (!trimmed) {
        return null;
    }

    const managerAccountMatch = trimmed.match(/^Paid to Manager'?s Account \((.+?)\)/i)
        || trimmed.match(/^Paid by Manager \((.+?)\)/i);
    if (managerAccountMatch) {
        return `Paid to Manager's Account (${managerAccountMatch[1].trim()})`;
    }

    const paidToMatch = trimmed.match(/^Paid to (.+)$/i);
    if (paidToMatch) {
        return `Paid to Manager's Account (${paidToMatch[1].trim()})`;
    }

    const directMatch = trimmed.match(/^Paid directly to (.+?)'s Stripe account/i);
    if (directMatch) {
        return `Paid Directly to ${directMatch[1].trim()}'s Stripe account`;
    }

    return null;
}

/** Extract manager display name from manager-routed ManualPayReason strings. */
export function extractManagerRecipientName(manualPayReason) {
    if (manualPayReason === null || manualPayReason === undefined) {
        return null;
    }
    const trimmed = String(manualPayReason).trim();
    if (!trimmed) return null;

    const pendingMatch = trimmed.match(
        /^Pending payment to employee \(paid to manager\)(?:\s*\((.+?)\))?/i,
    );
    if (pendingMatch) {
        return pendingMatch[1] ? pendingMatch[1].trim() : null;
    }

    const managerAccountMatch = trimmed.match(/^Paid to Manager'?s Account \((.+?)\)/i)
        || trimmed.match(/^Paid by Manager \((.+?)\)/i);
    if (managerAccountMatch) {
        return managerAccountMatch[1].trim();
    }

    return null;
}

function normalizeApiObligationUpdate(raw, fallbackDate = new Date()) {
    if (!raw) return null;

    const userId = raw.userId ?? raw.UserId;
    const workPeriodId = raw.workPeriodId ?? raw.WorkPeriodId;
    if (!userId || !workPeriodId) return null;

    const amountPaid = roundCurrency(raw.amountPaid ?? raw.AmountPaid ?? 0);
    const amountOwedBefore = roundCurrency(raw.amountOwedBefore ?? raw.AmountOwedBefore ?? 0);
    let status = String(raw.status ?? raw.Status ?? 'paid').toLowerCase();
    const payoutPath = String(raw.payoutPath ?? raw.PayoutPath ?? '').toLowerCase();
    const recipientName = raw.recipientName ?? raw.RecipientName ?? raw.recipientUsername ?? raw.RecipientUsername ?? '';
    const employeeName = raw.employeeName ?? raw.EmployeeName ?? raw.username ?? raw.Username ?? '';

    let manualPayReason = raw.manualPayReason ?? raw.ManualPayReason;
    if (!manualPayReason && amountPaid > 0) {
        if (payoutPath === PAYOUT_PATH_DIRECT) {
            manualPayReason = formatDirectStripePayReason(employeeName || recipientName, amountPaid, fallbackDate);
        } else {
            manualPayReason = formatManagerReleasePayReason(recipientName, amountPaid, amountOwedBefore, fallbackDate);
        }
    }

    if (payoutPath === PAYOUT_PATH_MANAGER && (status === 'paid' || status === 'pending_paid_to_manager')) {
        status = 'pending_paid_to_manager';
    }

    const tipsOwedAmount = raw.tipsOwedAmount ?? raw.TipsOwedAmount;
    let resolvedTipsOwed = tipsOwedAmount !== undefined && tipsOwedAmount !== null
        ? roundCurrency(tipsOwedAmount)
        : Math.max(0, roundCurrency(amountOwedBefore - amountPaid));

    let tipsPaidAmount = amountPaid;
    if (status === 'pending_paid_to_manager') {
        tipsPaidAmount = 0;
        resolvedTipsOwed = amountOwedBefore > 0
            ? amountOwedBefore
            : (resolvedTipsOwed > 0 ? resolvedTipsOwed : amountPaid);
    }

    return {
        userId: Number(userId),
        workPeriodId: Number(workPeriodId),
        saleId: raw.saleId ?? raw.SaleId ?? `${userId}-${workPeriodId}`,
        status,
        manualPayReason,
        tipsPaidAmount,
        tipsOwedAmount: resolvedTipsOwed,
    };
}

export function collectReleasableObligations(tipPayoutSettings, directRecipients = []) {
    return collectUnpaidObligationsFromTipPayoutSettings(tipPayoutSettings, directRecipients);
}

export function collectUnpaidObligationsFromTipPayoutSettings(
    tipPayoutSettings,
    directRecipients = [],
) {
    const byKey = new Map();

    const mergeObligation = (userId, workPeriodId, amountOwed, username = '') => {
        const normalizedAmount = roundCurrency(amountOwed);
        if (!userId || !workPeriodId || normalizedAmount <= 0) {
            return;
        }

        const key = `${userId}-${workPeriodId}`;
        const existing = byKey.get(key);
        if (existing) {
            existing.amountOwed = roundCurrency(existing.amountOwed + normalizedAmount);
            if (!existing.recipientName && username) {
                existing.recipientName = username;
            }
            return;
        }

        byKey.set(key, {
            userId: Number(userId),
            workPeriodId: Number(workPeriodId),
            saleId: key,
            amountOwed: normalizedAmount,
            amountCleared: normalizedAmount,
            recipientName: username,
        });
    };

    (tipPayoutSettings?.releasableTipsBreakdown || []).forEach((row) => {
        mergeObligation(row.userId, row.workPeriodId, row.amountReleasable, row.username);
    });

    (tipPayoutSettings?.pendingTipsBreakdown || []).forEach((row) => {
        mergeObligation(row.userId, row.workPeriodId, row.amountOwed, row.username);
    });

    (directRecipients || []).forEach((recipient) => {
        (recipient.workPeriodBalances || []).forEach((workPeriod) => {
            mergeObligation(
                recipient.userId,
                workPeriod.workPeriodId,
                workPeriod.amountOwed,
                recipient.username,
            );
        });
    });

    return Array.from(byKey.values());
}

export function computeProportionalPaidUpdates(
    obligations,
    releaseAmount,
    recipientName,
    payoutPath = PAYOUT_PATH_MANAGER,
    date = new Date(),
) {
    const pool = (obligations || []).filter((item) => item.amountOwed > 0);
    const normalizedReleaseAmount = roundCurrency(releaseAmount);

    if (pool.length === 0 || normalizedReleaseAmount <= 0) {
        return [];
    }

    const totalOwed = pool.reduce((sum, item) => sum + item.amountOwed, 0);
    if (totalOwed <= 0) {
        return [];
    }

    let allocated = 0;
    const updates = [];

    pool.forEach((obligation, index) => {
        let amountPaid;
        if (index === pool.length - 1) {
            amountPaid = roundCurrency(normalizedReleaseAmount - allocated);
        } else {
            amountPaid = roundCurrency((normalizedReleaseAmount * obligation.amountOwed) / totalOwed);
            allocated = roundCurrency(allocated + amountPaid);
        }

        const status = payoutPath === PAYOUT_PATH_DIRECT
            ? (amountPaid >= obligation.amountOwed ? 'paid' : 'partial')
            : (amountPaid >= obligation.amountOwed ? 'pending_paid_to_manager' : 'partial');
        const tipsOwedAmount = payoutPath === PAYOUT_PATH_DIRECT
            ? Math.max(0, roundCurrency(obligation.amountOwed - amountPaid))
            : (amountPaid >= obligation.amountOwed
                ? obligation.amountOwed
                : Math.max(0, roundCurrency(obligation.amountOwed - amountPaid)));
        const tipsPaidAmount = payoutPath === PAYOUT_PATH_DIRECT ? amountPaid : 0;
        const manualPayReason = payoutPath === PAYOUT_PATH_DIRECT
            ? formatDirectStripePayReason(obligation.recipientName || recipientName, amountPaid, date)
            : formatManagerReleasePayReason(recipientName, amountPaid, obligation.amountOwed, date);

        updates.push({
            userId: obligation.userId,
            workPeriodId: obligation.workPeriodId,
            saleId: obligation.saleId,
            status,
            manualPayReason,
            tipsPaidAmount,
            tipsOwedAmount,
        });
    });

    return updates;
}

export function resolvePaidUpdatesFromManagerRelease(apiResponse, releaseContext = {}) {
    const result = apiResponse?.result || apiResponse?.Result || {};
    const obligationUpdates = result.obligationUpdates
        || result.ObligationUpdates
        || apiResponse?.obligationUpdates
        || apiResponse?.ObligationUpdates;

    if (Array.isArray(obligationUpdates) && obligationUpdates.length > 0) {
        return obligationUpdates
            .map((item) => normalizeApiObligationUpdate(item))
            .filter(Boolean);
    }

    const settings = releaseContext.tipPayoutSettings;
    const releaseAmount = roundCurrency(
        result.amountReleased
        ?? result.AmountReleased
        ?? releaseContext.releaseAmount
        ?? 0,
    );
    const recipientName = releaseContext.recipientName
        || result.recipientName
        || result.RecipientName
        || settings?.designatedRecipient?.username
        || settings?.designatedRecipient?.Username
        || '';
    const payoutPath = releaseContext.payoutPath || PAYOUT_PATH_MANAGER;

    if (!settings || releaseAmount <= 0) {
        return [];
    }

    const obligations = collectUnpaidObligationsFromTipPayoutSettings(
        settings,
        releaseContext.directRecipients,
    );
    return computeProportionalPaidUpdates(
        obligations,
        releaseAmount,
        recipientName,
        payoutPath,
        new Date(),
    );
}

export function buildPaidUpdatesFromDirectEmployeePayResult(result, fallbackRecipientName) {
    if (!result) return [];

    const userId = result.userId ?? result.UserId;
    const username = result.username ?? result.Username ?? fallbackRecipientName ?? '';
    const workPeriodResults = result.workPeriodResults ?? result.WorkPeriodResults;

    if (Array.isArray(workPeriodResults) && workPeriodResults.length > 0) {
        return workPeriodResults
            .map((item) => {
                const amountPaid = roundCurrency(item.amountPaid ?? item.AmountPaid ?? 0);
                const amountRemaining = roundCurrency(item.amountRemaining ?? item.AmountRemaining ?? 0);
                const workPeriodId = item.workPeriodId ?? item.WorkPeriodId;
                if (!userId || !workPeriodId || amountPaid <= 0) {
                    return null;
                }

                return buildDirectStripePaidUpdate(
                    userId,
                    workPeriodId,
                    `${userId}-${workPeriodId}`,
                    username,
                    amountPaid,
                    amountPaid + amountRemaining,
                );
            })
            .filter(Boolean);
    }

    const amountPaid = roundCurrency(result.amountPaid ?? result.AmountPaid ?? 0);
    if (!userId || amountPaid <= 0) {
        return [];
    }

    const directUpdate = buildDirectStripePaidUpdate(
        userId,
        null,
        null,
        username,
        amountPaid,
    );

    return directUpdate ? [directUpdate] : [];
}

export function buildDirectStripePaidUpdate(
    userId,
    workPeriodId,
    saleId,
    recipientDisplayName,
    totalAmount,
    amountOwedBefore,
) {
    const amountPaid = roundCurrency(totalAmount);
    if (!userId || !workPeriodId || amountPaid <= 0) {
        return null;
    }

    const owedBefore = roundCurrency(amountOwedBefore ?? amountPaid);
    const status = amountPaid >= owedBefore ? 'paid' : 'partial';
    const tipsOwedAmount = Math.max(0, roundCurrency(owedBefore - amountPaid));

    return {
        userId: Number(userId),
        workPeriodId: Number(workPeriodId),
        saleId: saleId || `${userId}-${workPeriodId}`,
        status,
        manualPayReason: formatDirectStripePayReason(recipientDisplayName, amountPaid),
        tipsPaidAmount: amountPaid,
        tipsOwedAmount: tipsOwedAmount,
    };
}
